import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import SimpleTable from "../../stories/Tables/SimpleTable";
import SearchForm from "../../stories/Forms/SearchForm";
import {
  testKitRegistrationFields,
  testKitRejectionFields,
  links,
  testApptRejectionFields,
  vhAppointmentConfirmationFields,
  vhConfirmationOneTubeFields,
  vhConfirmationTwoTubeFields
} from "../../stories/Forms/fields";
import useApis from "../../services/useAPI";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { Button } from "../../stories/Buttons/Button";
import FormInput from "../../stories/Forms/FormInput";
import { Col, Modal, Row, Spinner } from "react-bootstrap";
import SuccessModal from "../../stories/Modals/Modal";
import ConfirmationModal from "../../stories/Modals/ConfirmationModal";
import {
  formatDtAge,
  formatDate,
  TimeFormatter,
  extractTime,
  trimSpaces,
  formatRejectionDate,
  formatEditDate,
  localTimeFormatter,
  formatTestType,
} from "../../utils/utils";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import { rejectValidationSchema } from "../../stories/Forms/validationSchema";
import Toaster from "../../stories/Toasts/Toaster";
import {
  ArrowRightCircleFill,
  XCircleFill,
  CheckCircleFill,
  InfoCircleFill,
} from "react-bootstrap-icons";
import PropTypes from "prop-types";
import DetailCard from "../../stories/Cards/DetailCard";
import PageLayout from "../../components/Layout/PageLayout";
import Notes from "../../stories/Forms/Notes";
import VHConfirmationPopup from "../../stories/Modals/VHConfirmationPopup";
import VHSubmitConfirmModal from "../../stories/Modals/VHSubmitConfirmModal";

const RejectionForm = ({
  notes,
  handleNotesChange,
  handleSaveClick,
  handleCancelClick,
  isEditMode,
  fields,
  handleBackClick,
  handleSearchFormSubmit,
  selectedService,
}) =>
  !isEditMode ? (
    <SearchForm
      data={notes}
      fields={fields}
      onBackClick={handleBackClick}
      onSubmit={handleSearchFormSubmit}
      validationSchema={rejectValidationSchema}
      title={""}
      subtitle={
        <p className="d-flex ageo-normal">
          <InfoCircleFill className="me-2" />
          Please also follow your organisation’s reporting processes for any kit
          or appointment incidents.
        </p>
      }
    />
  ) : (
    <Row>
      <Col md={12}>
        <FormInput
          as="textarea"
          className="notes-textarea"
          label=""
          name="notes"
          maxLength={256}
          onChange={handleNotesChange}
          placeholder="Enter details"
          type="text"
          value={notes?.notes || " "}
        />
      </Col>
      <Col md={6}>
        <div className="confirm-details-buttons">
          <Button
            label="Cancel"
            onClick={handleCancelClick}
            size="small"
            variant="danger"
          />
          <Button
            label="Save"
            onClick={handleSaveClick}
            size="small"
            variant="secondary"
          />
        </div>
      </Col>
    </Row>
  );

RejectionForm.propTypes = {
  notes: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  handleNotesChange: PropTypes.func.isRequired,
  handleSaveClick: PropTypes.func.isRequired,
  handleCancelClick: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleBackClick: PropTypes.func.isRequired,
  handleSearchFormSubmit: PropTypes.func.isRequired,
  selectedService: PropTypes.string,
};

const RegistrationForm = ({
  handleBackClick,
  handleEditClick,
  handleRegister,
  notes,
  handleNotesChange,
  handleSaveClick,
  handleCancelClick,
  isEditMode,
  children,
  formikRef,
}) =>
  isEditMode ? (
    <Row>
      {children}
      <Col md={12}>
        <FormInput
          as="textarea"
          className="notes-textarea w-100"
          label="Notes"
          maxLength={256}
          name="notes"
          onChange={handleNotesChange}
          placeholder="Enter details"
          type="text"
          value={notes}
          required={false}
        />
      </Col>
      <Col md={12}>
        <div className="confirm-details-buttons gap-2 outside-actions">
          <Button
            label="Cancel"
            onClick={handleCancelClick}
            size="none"
            variant="danger"
            className="btn-rounded semi-buttons"
          />
          <Button
            label="Save"
            onClick={handleSaveClick}
            size="none"
            variant="primary"
            className="btn-rounded semi-buttons"
          />
        </div>
      </Col>
    </Row>
  ) : (
    <SearchForm
      formikRef={formikRef}
      title=""
      subtitle=""
      fields={testKitRegistrationFields}
      onBackClick={handleBackClick}
      onEditClick={handleEditClick}
      onSubmit={handleRegister}
      values={notes}
    >
      {children}
    </SearchForm>
  );

RegistrationForm.propTypes = {
  handleBackClick: PropTypes.func.isRequired,
  handleEditClick: PropTypes.func.isRequired,
  handleRegister: PropTypes.func.isRequired,
  notes: PropTypes.string,
  handleNotesChange: PropTypes.func.isRequired,
  handleSaveClick: PropTypes.func.isRequired,
  handleCancelClick: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  children: PropTypes.node,
  formikRef: PropTypes.object,
};

const renderButtonLabel = (isLoading) =>
  isLoading ? (
    <div className="spinner-border spinner-border-sm" role="status">
      <span className="sr-only">Loading...</span>
    </div>
  ) : (
    "Register"
  );

const getRejectionMessage = (kitRejection, barcodeValue) => {
  const type = kitRejection
    ? "test kit with barcode"
    : "appointment with booking ID";

  const value = kitRejection
    ? barcodeValue
    : localStorage.getItem("booking_id");

  return `Confirm that you are rejecting the ${type}: ${value}`;
};

/*
 * @description This component is used to confirm the details of a patient's appointment
 * @param {string} id - The id of the appointment
 * @returns {JSX.Element} - The JSX element representing the component
 */

function AppointmentConfirmation() {
  const auth = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const postUrl = `/api/phlebotomists-portal/test-association/${id}/`;
  const putUrlReject = `/api/phlebotomists-portal/kit-rejection`;
  const [submitSuccess, setSubmitSuccess] = useState();
  const [notes, setNotes] = useState("");
  const [barcodeValue, setBarcodeValue] = useState("");
  const location = useLocation();
  const { rejectPath } = location.state || {};
  const formikRef = useRef(null);
  const study = location.state?.study;
  const [test, setTest] = useState("");

  const isVhLogin = auth?.featureFlag;


  const {
    callApi,
    resultGet,
    resultPost,
    isLoading,
    serverError,
    message,
    modalShow,
    tokenError,
    setModalShow,
    toggleShowError,
  } = useApis();
  const { callApi: callAssociationAPI } = useApis();
  const [tableData, setTableData] = useState({
    full_name: "",
    booking_id: "",
    dob: "",
    test_type: "",
    primary_barcode: "",
    secondary_barcode: "",
    sample_collection_date: "",
    sample_collection_time: "",
    number_of_attempt: 0,
    store_location: "",
    rejection_reason: "",
    notes: "",
  });

  const [initialTableData, setInitialTableData] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const [showToaster, setShowToaster] = useState(false);
  const [kitRejection, setKitRejection] = useState(false);
  const [appointmentRejection, setAppointmentRejection] = useState(false);
  const [fields, setFields] = useState([]);
  let selectedService = localStorage.getItem("selectedService");
  const isVhClinical = isVhLogin;
  const isVhSaveRef = useRef(false);

  const initializeData = useCallback(() => {
    if (resultGet) {
      setBarcodeValue(resultGet?.primary_barcode);
      const blurredValues = {
        secondary_barcode: localStorage.getItem("secondary_barcode"),
        sample_collection_date: formatDate(
          localStorage.getItem("sample_collection_date"),
        ),
        sample_collection_time: localStorage.getItem("sample_collection_time"),
        number_of_attempts: localStorage.getItem("number_of_attempt"),
      };

      localTimeFormatter(
        resultGet.sample_collection_date,
        resultGet.sample_collection_time,
      ).then((formattedDateTime) => {
        const initialData = {
          full_name: resultGet.full_name,
          booking_id:
            resultGet.booking_id || localStorage.getItem("booking_id"),
          dob: resultGet.dob && formatDtAge(resultGet.dob, isVhLogin),
          test_type: resultGet.test_type,
          primary_barcode: resultGet.primary_barcode,
          secondary_barcode:
            resultGet.secondary_barcode || blurredValues.secondary_barcode,
          sample_collection_date:
            formatDate(resultGet.sample_collection_date) ||
            formatDate(blurredValues.sample_collection_date),
          sample_collection_time:
            extractTime(formattedDateTime) ||
            blurredValues.sample_collection_time,
          number_of_attempts:
            resultGet.number_of_attempts || blurredValues.number_of_attempts,
          store_location: resultGet.store_location,
          notes: resultGet.notes || blurredValues.notes,
          patient_id: resultGet.patient_id
        };
        setInitialTableData(initialData);
        setTableData(initialData);
      });
    }
  }, [resultGet]);


  const stages = ["patient-details","enter-barcode","appointment-confirmation"];
  
  useEffect(() => {
    if (!stages.includes(sessionStorage.getItem("allowedStage")) && auth?.featureFlag) {
      navigate("/patient-record");
    }
  },[sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  useEffect(() => {
    if (rejectPath && !kitRejection && !appointmentRejection) {
      setShowRejectionModal(true);
    }
    if (auth && selectedService === "clinical") {
      auth.setTitle("APPOINTMENT CONFIRMATION");
    }
  }, [auth, rejectPath, kitRejection, appointmentRejection]);

  useEffect(() => {
    if (resultGet?.notes) {
      setNotes(resultGet?.notes);
    }
    if (selectedService === "healthCare") {
      auth.setTitle(
        `In-store Wellness Testing Service - ${resultGet?.test_type}`,
      );
    }
    setTest(resultGet?.test_type);
  }, [resultGet]);

  useEffect(() => {
    fetchData();
  }, [id, location.state?.testkit]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  useEffect(() => {
    if (resultPost) {
      handlePostResult();
    }
  }, [resultPost]);

  // Automatically hides toaster after 3 seconds
  useEffect(() => {
    if (showToaster) {
      const timer = setTimeout(() => {
        setShowToaster(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showToaster]);
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Fetch appointment data from API
  const fetchData = useCallback(() => {
    if (!auth?.accessToken) return;

    const testkit = location.state?.testkit || id;
    const url = rejectPath
      ? `/api/phlebotomists-portal/kit-rejection/${testkit}/?bid=${
          location.pathname.split("/")[2]
        }`
      : `/api/phlebotomists-portal/appointment-confirmation/${id}/`;
    // Call the API with the payload
    callApi(null, url, "GET", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
  }, [
    id,
    location.state?.testkit,
    rejectPath,
    location.pathname,
    auth,
    callApi,
  ]);

  //Handle the API response
  const handlePostResult = () => {
    setIsEditMode(false);
    fetchData();
    if (resultPost.reject_flag) {
      navigate(
        kitRejection ? `/enter-barcode/${id}?reload=true` : `/patient-record/`,
      );
    }
    localStorage.removeItem("sample_collection_time");
    localStorage.removeItem("sample_collection_date");
    localStorage.removeItem("number_of_attempt");
  };

  const handleBackClick = () => {
    localStorage.removeItem("sample_collection_time");
    localStorage.removeItem("sample_collection_date");
    localStorage.removeItem("number_of_attempt");
    navigate(`/enter-barcode/${id}`);
  };

  const handleRejectionModalClose = () => {
    setShowRejectionModal(false);
    navigate(-1);
    auth?.showToast(
      "Rejection Cancelled",
      "You cancelled the rejection process",
      "../assets/mhc.png",
    );
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setUnsavedChanges(true);
  };

  const handleKit = () => {
    setFields(testKitRejectionFields);
    setKitRejection(true);
    setShowRejectionModal(false);
  };

  const handleAppointment = useCallback(() => {
    setFields(testApptRejectionFields);
    setAppointmentRejection(true);
    setShowRejectionModal(false);
  }, []);

  // Save changes and update the appointment
  const handleSaveClick = async () => {
    const formattedDateTime = await TimeFormatter(
      formatEditDate(tableData.sample_collection_date),
      tableData.sample_collection_time,
    );

    // Create the payload object with formatted datetime
    const payload = {
      ...tableData,
      sample_collection_date: formatEditDate(tableData.sample_collection_date),
      sample_collection_time: formattedDateTime,
      notes,
      number_of_attempt: tableData.number_of_attempts,
      primary_barcode: trimSpaces(tableData.primary_barcode),
      is_clinical: selectedService === "clinical",
      study,
    };

    isVhSaveRef.current = isVhClinical;

    // Call the API with the payload
    await callApi(
      payload,
      `/api/phlebotomists-portal/appointment-confirmation/${id}/`,
      "PUT",
      {
        Authorization: `Bearer ${
          localStorage.getItem("access_token") || auth.accessToken
        }`,
        "Content-Type": "application/json",
      },
    );
    setShowToaster(true);
  };

  // Cancel changes and revert to initial data
  const handleCancelClick = () => {
    unsavedChanges ? setShowConfirmationModal(true) : setIsEditMode(false);
  };

  // Handle table cell edit
  const handleCellEdit = (fieldName, value) => {
    setTableData((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === "sample_collection_date"
          ? formatDate(value)
          : fieldName === "number_of_attempts"
            ? Number.parseInt(value, 10)
            : value,
    }));
    setUnsavedChanges(true);
  };

  // Handle notes field change
  const handleNotesChange = (event) => {
     const value =
    typeof event === "string"
      ? event
      : event?.target?.value ?? "";

  setNotes(value);
    setUnsavedChanges(true);
  };

  // Handle appointment rejection
  const handleRejection = async (formData) => {
    const formattedTime = new Date(formData.sample_collection_time);
    const confirmedData = {
      reject_flag: true,
      ...formData,
      ...tableData,
      sample_collection_time: formattedTime,
      testkit: location.state?.testkit,
      notes: formData.notes,
      bid: id,
      rejection_type: appointmentRejection ? "appointment" : "test_kit",
    };

    callApi(confirmedData, putUrlReject, "PUT", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
    localStorage.removeItem("secondary_barcode");
    setUnsavedChanges(false);
    setModalShow(false);
    navigate(
      appointmentRejection
        ? `/patient-record/`
        : `/enter-barcode/${id}?reload=true`,
    );
    auth?.showToast(
      "Rejection Successful",
      appointmentRejection
        ? "Appointment has been rejected successfully."
        : "Test kit has been rejected successfully.",
      "../assets/mhc.png",
      {icon: <XCircleFill />, className: auth?.featureFlag && "vh-toaster"},
    );
  };

  // Handles form submission for registration
  const handleRegister = async (formData) => {
    const confirmedData = {
      ...formData,
      ...tableData,
      notes,
      number_of_attempt: tableData.number_of_attempts,
    };
    confirmedData.test_type = formatTestType(tableData.test_type);

    await callApi(confirmedData, postUrl, "POST", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,
      "Content-Type": "application/json",
    });
    auth?.allowStage("appointment-confirmation");
    setUnsavedChanges(false);
  };

  useEffect(() => {
    if (isVhSaveRef.current && message?.postMessage) {
      isVhSaveRef.current = false;
      auth?.showToast(
        "Success",
        message.postMessage,
        "../assets/mhc.png",
        { icon: <CheckCircleFill />, className: "vh-toaster" },
      );
      setUnsavedChanges(false);
      setTimeout(() => setIsEditMode(false));
      return;
    }
    if (message.postMessage === "Success: Your changes have been saved") {
      setSubmitSuccess(true);
      setTimeout(() => setIsEditMode(false));
    } else {
      toggleShowError();
    }
  }, [message]);

  // VH clinical: show save-changes API errors as a toast
  useEffect(() => {
    if (isVhSaveRef.current && serverError) {
      isVhSaveRef.current = false;
      auth?.showToast(
        "Error",
        serverError,
        "../assets/mhc.png",
        { icon: <XCircleFill />, className: "vh-toaster" },
      );
    }
  }, [serverError]);

  useEffect(() => {
    if (resultPost?.[0]?.booking_reference_id) {
      auth?.showToast(
        "Confirm user details",
        "User details confirmed!",
        "../assets/mhc.png",
        {icon: <ArrowRightCircleFill />},
      );
      if (selectedService === "clinical" && auth?.featureFlag) {
        // VH clinical: checklist was already done before this page, so registration is complete
        auth?.showToast(
          "Success",
          "The test has been successfully registered.",
          "../assets/mhc.png",
          {icon: <ArrowRightCircleFill />, className: "vh-toaster"},
        );
        localStorage.removeItem("secondary_barcode");
        navigate("/patient-record", { replace: true });
      } else if (selectedService === "clinical") {
        navigate(`/clinical-confirmation-steps/${id}`, {
          state: { secondary_barcode: resultGet?.secondary_barcode },
        });
      } else {
        navigate(`/confirmation-steps/${id}`, {
          state: {
            secondary_barcode: resultGet?.secondary_barcode,
            testType: test,
          },
        });
      }
    }
  }, [resultPost]);

  // Warn user of unsaved changes before unloading the page
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (unsavedChanges) event.preventDefault();
    };
    globalThis.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      globalThis.removeEventListener("beforeunload", handleBeforeUnload);
  }, [unsavedChanges]);

  // Confirm leaving without saving changes
  const handleConfirmLeave = () => {
    setShowConfirmationModal(false);
    setTableData(initialTableData);
    setIsEditMode(false);
    setUnsavedChanges(false);
  };

  const handleSearchFormSubmit = (formData) => {
    setSubmittedFormData(formData);
    setModalShow(true);
  };

  const onToasterClose = () => {
    setShowToaster(false);
  };

  const buttonLabel = renderButtonLabel(isLoading);

  return isVhLogin ? (
    
      <LoggedInLayout
        buttonLabel={"Logout"}
        img={img}
        isActivation={false}
        isVhlogin={isVhLogin}
        links={links}
        logo={logo}
        pageTitle={auth.title}
        userInitials={auth.userInitials}
        vitalityLayout={isVhLogin}
      >
        <PageLayout
          heading="Test confirmation"
          subHeading={
            <>
              Confirm sample details. Use <b>‘Edit’</b> to update fields. Click{" "}
              <b>‘Submit’</b> to submit the test order.
            </>
          }
          className={`${selectedService === "clinical" ? "clinical-details-card " : ""}vh-clinical-appointment-confirmation d-flex flex-column`}
          bodyClassName="d-flex flex-column"
        >
          <div className="vh-appointment-confirmation-card card">
            <DetailCard
              heading={selectedService === "clinical" ? "" : "Patient details"}
              data={tableData}
              fields={
                selectedService === "clinical"
                  ? vhAppointmentConfirmationFields
                  : resultGet?.secondary_barcode
                    ? vhConfirmationTwoTubeFields
                    : vhConfirmationOneTubeFields
              }
              editable={isVhClinical && isEditMode}
              onUpdate={handleCellEdit}
            />
            <hr />
            <Notes
              value={notes}
              onChange={handleNotesChange}
              disabled={!isEditMode}
            />
          </div>
          <div className="info-button-section d-flex justify-content-end">
            <div className="btn-section d-flex">
              {isVhClinical && (
                <button
                  className="boots-secondary-btn-styling"
                  disabled={isLoading}
                  onClick={isEditMode ? handleSaveClick : handleEditClick}
                >
                  {isEditMode ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="11"
                      viewBox="0 0 14 11"
                      fill="none"
                    >
                      <path
                        d="M13.0607 1.06066L4.03033 10.091L0 6.06066L1.06066 5L4.03033 7.96967L12 0L13.0607 1.06066Z"
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        fill="#05054B"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                    >
                      <path
                        d="M9 2.17067H3C2.44788 2.17067 2.00026 2.61861 2 3.17067V15.1707C2 15.723 2.44772 16.1707 3 16.1707H15C15.5523 16.1707 16 15.723 16 15.1707V9.17067H18V15.1707C18 16.8275 16.6569 18.1707 15 18.1707H3C1.34315 18.1707 0 16.8275 0 15.1707V3.17067C0.000263389 1.51404 1.34331 0.170668 3 0.170668H9V2.17067ZM12.8789 0.878676C14.0505 -0.29289 15.9495 -0.292894 17.1211 0.878676L17.293 1.04957C18.4645 2.22115 18.4645 4.12117 17.293 5.29274L10.9717 11.614L5.82129 12.3494L6.55664 7.19997L12.8789 0.878676ZM8.44238 8.14235L8.17871 9.99293L10.0283 9.72828L14.0859 5.67164L12.5 4.08571L8.44238 8.14235ZM15.707 2.29274C15.3165 1.90222 14.6835 1.90222 14.293 2.29274L13.9141 2.67164L15.5 4.25661L15.8789 3.87868C16.2694 3.48815 16.2694 2.85416 15.8789 2.46364L15.707 2.29274Z"
                        fill="#05054B"
                      />
                    </svg>
                  )}

                  {isEditMode ? "Save changes" : "Edit"}
                </button>
              )}
              <button
                className="boots-primary-btn-styling"
                disabled={isEditMode}
                onClick={() => setShowSubmitConfirm(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M18.0605 12L11.5303 18.5303L10.4697 17.4697L15.1895 12.75H6V11.25H15.1895L10.4697 6.53027L11.5303 5.46973L18.0605 12Z"
                    fill="white"
                  />
                </svg>
                Submit
              </button>
            </div>
          </div>
        </PageLayout>
        <VHSubmitConfirmModal
          show={showSubmitConfirm}
          participantName={tableData?.full_name}
          isLoading={isLoading}
          onCancel={() => setShowSubmitConfirm(false)}
          onConfirm={() => {
            setShowSubmitConfirm(false);
            handleRegister();
            navigate("/consultation-success", {
              state: {
                isSuccess: true,
                rejectionMessage: "Success",
                isClinical: selectedService === "clinical",
                serviceType: "clinical",
                isHealthcare: selectedService === "healthCare",
                id: {id}
              },
              replace: true,
            });
          }}
        />
      </LoggedInLayout>
    
  ) : (
    <LoggedInLayout
      buttonLabel={"Logout"}
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
    >
      {rejectPath ? (
        <div className="appointment-rejection">
          <div className="appointment-rejection-wrapper">
            <h2 className="test-registration-description">
              Are you sure you want to {kitRejection ? "reject" : "cancel"} the
              following {kitRejection ? "test kit" : "appointment"}?
            </h2>
            {isLoading && <p>Loading...</p>}
            {serverError && (
              <p className="server-error">Error: {serverError}</p>
            )}

            {Object.keys(tableData).length > 0 && (
              <>
                <SimpleTable
                  data={tableData}
                  editable={isEditMode}
                  onUpdate={handleCellEdit}
                  rejectPath={rejectPath}
                />
                <hr />
                <RejectionForm
                  notes={notes}
                  handleNotesChange={handleNotesChange}
                  handleSaveClick={handleSaveClick}
                  handleCancelClick={handleCancelClick}
                  isEditMode={isEditMode}
                  fields={fields}
                  handleBackClick={handleBackClick}
                  handleSearchFormSubmit={handleSearchFormSubmit}
                  selectedService={selectedService}
                />
              </>
            )}
          </div>

          {modalShow && (
            <SuccessModal
              label={"Confirm"}
              message={getRejectionMessage(kitRejection, barcodeValue)}
              onClick={() => handleRejection(submittedFormData)}
              onHide={() => setModalShow(false)}
              show={modalShow}
              variant={"danger"}
              heading="Confirm Rejection"
              close={true}
              isRejectiomnModal={true}
              wrapperClass="rejection-confirmation-modal"
            />
          )}

          {showRejectionModal && (
            <RejectionTypeModal
              show={showRejectionModal}
              onHide={handleRejectionModalClose}
              handleKit={handleKit}
              handleAppointment={handleAppointment}
            />
          )}
        </div>
      ) : (
        <>
          {serverError && <p className="server-error">Error: {serverError}</p>}

          {Object.keys(tableData).length > 0 && (
            <>
              {selectedService === "healthCare" ? (
                <>
                  <h3 className="patient-record-title">Test confirmation</h3>
                  <span>
                    <span className="desc-text">
                      Confirm sample details. Use &lsquo;Edit&rsquo; to update
                      fields. Click &lsquo;Register&rsquo; to submit the test
                      order.
                    </span>
                    <span className="imp-text">
                      IMPORTANT! Changes cannot be made after selecting
                      &lsquo;Register&rsquo;.
                    </span>
                  </span>
                </>
              ) : (
                ""
              )}
              <div
                className={` ${selectedService === "healthCare" ? "card mt-4 appointment-confirmation-card" : ""}`}
              >
                <RegistrationForm
                  formikRef={formikRef}
                  handleBackClick={handleBackClick}
                  handleEditClick={handleEditClick}
                  handleRegister={handleRegister}
                  notes={resultGet?.notes}
                  handleNotesChange={handleNotesChange}
                  handleSaveClick={handleSaveClick}
                  handleCancelClick={handleCancelClick}
                  isEditMode={isEditMode}
                >
                  <div
                    className={`clinical-wrapper ${selectedService === "clinical" && "px-3"} `}
                  >
                    <SimpleTable
                      data={tableData}
                      editable={isEditMode}
                      onUpdate={handleCellEdit}
                    />
                  </div>
                </RegistrationForm>
              </div>
              {isEditMode ? (
                ""
              ) : (
                <div className="outside-actions">
                  <Button
                    label="Back"
                    type="button"
                    variant="outline"
                    name="back"
                    required={false}
                    className="ms-auto btn-back btn-rounded"
                    onClick={handleBackClick}
                  />
                  <Button
                    label="Edit"
                    isDisabled={
                      resultGet?.test_kit_status === "activated" ||
                      resultGet?.test_kit_status === "quarantine-cleared"
                    }
                    type="button"
                    variant="secondary"
                    name="button"
                    required={false}
                    className="ms-auto btn-rounded semi-buttons"
                    size="none"
                    onClick={() => setIsEditMode(true)}
                  />
                  <Button
                    label={buttonLabel}
                    type="submit"
                    variant="primary"
                    name="button"
                    className="btn-rounded semi-buttons"
                    required={false}
                    size="none"
                    onClick={() => formikRef.current?.submitForm()}
                  />
                </div>
              )}
            </>
          )}

          {submitSuccess && (
            <SuccessModal
              href={"/"}
              message={"Booking details updated successfully"}
              onHide={() => setSubmitSuccess(false)}
              show={submitSuccess}
              variant={"success"}
              close={false}
            />
          )}

          {showToaster && (
            <Toaster
              body={serverError || "Details saved successfully"}
              header={serverError ? "Error" : "Success"}
              onClose={onToasterClose}
              position="top-end"
              show={showToaster}
            />
          )}
        </>
      )}

      <ConfirmationModal
        onCancelClick={handleConfirmLeave}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleConfirmLeave}
        heading={"Warning -Unsaved changes-any modifications will be lost. "}
        labelOne={"OK"}
        labelTwo={"CANCEL"}
        message={
          "Please click OK to continue without saving or Cancel to return and save your changes."
        }
        show={showConfirmationModal}
        variantOne={"secondary"}
        variantTwo={"danger"}
        close={false}
      />

      {tokenError && (
        <Toaster
          body={tokenError}
          header="Error"
          onClose={toggleShowError}
          position="top-end"
          show={tokenError}
        />
      )}
    </LoggedInLayout>
  );
}

const RejectionTypeModal = ({ show, onHide, handleKit, handleAppointment }) => (
  <Modal
    show={show}
    onHide={onHide}
    backdrop="static"
    keyboard={false}
    top
    className="rejection-modal"
  >
    <Modal.Header closeButton>
      <Modal.Title>
        {"Please select from these options for rejection:"}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>{"What do you want to reject?"}</Modal.Body>
    <Modal.Footer className="justify-center">
      <Button
        label={"Kit"}
        onClick={handleKit}
        size={"small"}
        variant={"primary"}
      />
      <Button
        label={"Appointment"}
        onClick={handleAppointment}
        size={"small"}
        variant={"danger"}
      />
    </Modal.Footer>
  </Modal>
);

RejectionTypeModal.propTypes = {
  handleKit: PropTypes.func.isRequired,
  handleAppointment: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default AppointmentConfirmation;
